<script lang="ts" setup>
import type { MoleculesShareButtonProps } from './MoleculesShareButton.props'

const props = withDefaults(defineProps<MoleculesShareButtonProps>(), {
  copiedDuring: 5000,
})

const { breakpoint } = useProjectBreakpoint()
const { copied, copy } = useClipboard({ copiedDuring: props.copiedDuring })

const isMobile = computed(() => breakpoint.smallerOrEqual('md').value)

const isShareApiSupported = ref(false)

onMounted(() => {
  // check by default if the Web Share API is supported, to avoid having to run the function on unsupported browsers
  isShareApiSupported.value =
    'share' in navigator && typeof navigator.share === 'function'
})

const isNativeShareInsteadOfShareTooltip = computed(
  () => isMobile.value && isShareApiSupported.value
)

const shareInProgress = ref(false)

const openSharePanel = async () => {
  if (!isNativeShareInsteadOfShareTooltip.value || !!shareInProgress.value)
    return

  try {
    // multiple share in progress throw an InvalidStateError
    shareInProgress.value = true
    await navigator.share({
      url: props.url,
    })
  } catch (e) {
    // user cancellation of the WebShare API throws an AbortError, which should be ignored
    if (e instanceof Error && e.name === 'AbortError') {
      return
    }
    // all other errors disable the Share button and enable the tooltip
    isShareApiSupported.value = false
  } finally {
    shareInProgress.value = false
  }
}
</script>

<template>
  <div class="share-button">
    <!-- openSharePanel is called only if the WebShare is available and on mobile, otherwise the click event is not fired -->
    <UtilsTooltip
      placement="bottom"
      :disabled="isNativeShareInsteadOfShareTooltip"
      :aria-label="ariaLabel ?? $ts('sharePopup.share')"
      @on-target-button-clicked="openSharePanel"
    >
      <template #target>
        <!-- Share button content with label -->
        <AtomsCta v-if="isShareAll" anatomy="link" type="div">
          <template #label>
            {{ buttonLabel ?? $ts('sharePopup.share') }}
          </template>
        </AtomsCta>
        <!-- Share button content with icon only  -->
        <DefaultIconsShare aria-hidden="true" />
      </template>
      <template #content>
        <div class="flex w-[10.5rem] flex-col gap-y-4 md:w-64">
          <p v-if="popupTitle" class="text-medium-6 uppercase">
            {{ popupTitle }}
          </p>
          <ul class="space-y-2">
            <li class="*:flex *:items-center *:gap-x-2">
              <p v-if="copied" class="text-link-6 underline" aria-live="polite">
                <DefaultIconsCheck aria-hidden="true" />
                <span>{{ $ts('sharePopup.linkCopied') }}</span>
              </p>
              <div v-else>
                <DefaultIconsCopyLinkIcon aria-hidden="true" />
                <AtomsCta anatomy="link" @click="copy(url)">
                  <template #label>
                    {{ $ts('sharePopup.copyLink') }}
                  </template>
                </AtomsCta>
              </div>
            </li>
            <li v-if="!sharingIconsPdp" class="flex items-center gap-x-2">
              <DefaultIconsWhatsapp aria-hidden="true" />
              <MoleculesLinkWrapper
                anatomy="link"
                :to="`https://api.whatsapp.com/send?text=${encodeURIComponent(url)}`"
                data-action="share/whatsapp/share"
                target="_blank"
              >
                <template #label>
                  {{ $ts('sharePopup.whatsapp') }}
                </template>
              </MoleculesLinkWrapper>
            </li>
            <li v-if="!sharingIconsPdp" class="flex items-center gap-x-2">
              <DefaultIconsMail aria-hidden="true" />
              <MoleculesLinkWrapper
                anatomy="link"
                :to="`mailto:?body=${encodeURIComponent(url)}`"
              >
                <template #label>
                  {{ $ts('sharePopup.email') }}
                </template>
              </MoleculesLinkWrapper>
            </li>
            <li v-if="sharingIconsPdp" class="flex items-center gap-x-2">
              <DefaultIconsFacebook aria-hidden="true" />
              <MoleculesLinkWrapper
                anatomy="link"
                :to="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`"
                target="_blank"
              >
                <template #label>
                  {{ $ts('sharePopup.facebook') }}
                </template>
              </MoleculesLinkWrapper>
            </li>
            <li v-if="sharingIconsPdp" class="flex items-center gap-x-2">
              <DefaultIconsXIcon aria-hidden="true" />
              <MoleculesLinkWrapper
                anatomy="link"
                :to="`http://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`"
                target="_blank"
              >
                <template #label>
                  {{ $ts('sharePopup.twitter') }}
                </template>
              </MoleculesLinkWrapper>
            </li>
          </ul>
        </div>
      </template>
    </UtilsTooltip>
  </div>
</template>

<style scoped lang="scss">
.share-button {
  :deep(.v-popper) {
    @apply flex items-center;
  }

  :deep(.tooltip-opener-button) {
    @apply flex items-center gap-x-2;
  }
}
</style>
